import { ColumnType } from 'ant-design-vue/es/table';
import { get, isEqual, set } from 'lodash-es';
import { computed, h, reactive, Ref, ref, toRaw, unref, watch } from 'vue';
import type { ActionItem, GetColumnsParams } from '@mts/components/Table';
import type { ComputedRef } from 'vue';
import {
  cloneDeep,
  isArray,
  isBoolean,
  isFunction,
  isIfShow,
  isString,
  isStringArray,
  omit,
} from '@mts-fe/utils';
import { t } from '@mts/hooks/web/useI18n';
import { usePermission } from '@mts/hooks/web/usePermission';
import { getGlobalParams } from '@mts/render';
import { mtsEmitter } from '@mts/utils';
import { EditableCell, formatCell, RenderCell, TableAction } from '../..';
import {
  ACTION_COLUMN_FLAG,
  DEFAULT_ALIGN,
  IGNORE_BTN_PROPS,
  INDEX_COLUMN_FLAG,
  PAGE_SIZE,
  ROW_KEY,
} from '../const';
import { getDataIndex, groupByGroupTitle } from '../helper';
import type { BasicColumn, BasicTableProps, PaginationProps } from '../types/table';

function handleItem(item: BasicColumn, ellipsis: boolean) {
  const { key, dataIndex, children } = item;
  item.align = item.align || DEFAULT_ALIGN;
  if (ellipsis) {
    if (!key) {
      item.key = getDataIndex(dataIndex);
    }
    if (!isBoolean(item.ellipsis)) {
      Object.assign(item, {
        ellipsis,
      });
    }
  }
  if (children && children.length) {
    handleChildren(children, !!ellipsis);
  }
}

function handleChildren(children: BasicColumn[] | undefined, ellipsis: boolean) {
  if (!children) return;
  children.forEach((item) => {
    const { children } = item;
    handleItem(item, ellipsis);
    handleChildren(children, ellipsis);
  });
}

function handleIndexColumn(
  propsRef: ComputedRef<BasicTableProps>,
  getPaginationRef: ComputedRef<boolean | PaginationProps>,
  columns: BasicColumn[],
) {
  const { showIndexColumn, indexColumnProps, isTreeTable } = unref(propsRef);

  if (unref(isTreeTable)) {
    return;
  }

  const indIndex = columns.findIndex((column) => column.flag === INDEX_COLUMN_FLAG);
  if (indIndex !== -1) {
    columns.splice(indIndex, 1);
  }

  if (!showIndexColumn) return;

  const isFixedLeft = columns.some((item) => item.fixed === 'left');

  columns.unshift({
    flag: INDEX_COLUMN_FLAG,
    width: 60,
    dataLangKey: 'component.table.index',
    title: t('component.table.index'),
    align: 'center',
    customRender: ({ index }) => {
      const getPagination = unref(getPaginationRef);
      if (isBoolean(getPagination)) {
        return `${index + 1}`;
      }
      const { current = 1, pageSize = PAGE_SIZE } = getPagination;
      return ((current < 1 ? 1 : current) - 1) * pageSize + index + 1;
    },
    ...(isFixedLeft
      ? {
          fixed: 'left',
        }
      : {}),
    ...indexColumnProps,
  });
}

export function useColumns(
  propsRef: ComputedRef<BasicTableProps>,
  getPaginationRef: ComputedRef<boolean | PaginationProps>,
  isMounted: Ref<boolean>,
) {
  const columnsRef = ref(unref(propsRef).columns) as unknown as Ref<BasicColumn[]>;
  let cacheColumns = unref(propsRef).columns;

  const getColumnsRef = computed(() => {
    const columns = cloneDeep(unref(columnsRef));

    handleIndexColumn(propsRef, getPaginationRef, columns);
    handleActionColumn(columns);
    if (!columns) {
      return [];
    }
    const { ellipsis } = unref(propsRef);

    columns.forEach((item) => {
      const { customRender, slots } = item;
      item.dataLangKey =
        item.dataLangKey ||
        (typeof item.title === 'string' && t(item.title) !== item.title ? item.title : '');
      item.title = typeof item.title === 'string' ? t(item.title) : item.title;
      handleItem(
        item,
        Reflect.has(item, 'ellipsis') ? !!item.ellipsis : !!ellipsis && !customRender && !slots,
      );
    });
    return columns;
  });

  const actionsRef = ref(unref(propsRef).actions || []);
  watch(
    () => unref(propsRef).actions,
    (actions) => {
      actionsRef.value = actions || [];
      // toggleActionColumn(!!actionsRef.value.length);
    },
  );

  const actionColumnShow = ref(true);
  /** 修改操作列的显示与隐藏 */
  const toggleActionColumn = (show?: boolean) =>
    (actionColumnShow.value = show ?? !actionColumnShow.value);

  function handleActionColumn(columns: BasicColumn[]) {
    const { actionColumn } = unref(propsRef);
    if (!actionColumn) return;

    const hasIndex = columns.findIndex((column) => column.flag === ACTION_COLUMN_FLAG);
    if (hasIndex === -1) {
      columns.push({
        ...columns[hasIndex],
        dataIndex: 'ACTION',
        dataLangKey: 'common.operating',
        title: t('common.operating'),
        fixed: 'right',
        align: 'left',
        width: 100,
        ifShow: () => actionColumnShow.value,
        ...actionColumn,
        flag: ACTION_COLUMN_FLAG,
      });
    }
  }

  const formatActions = (record: Recordable, index: number) => {
    const { actionHandler } = unref(propsRef);
    const globalParams = getGlobalParams();
    return cloneDeep(unref(actionsRef))
      .filter((action) =>
        action.ifBtnShow
          ? action.ifBtnShow({ column: action, record, index, ...globalParams })
          : true,
      )
      .map((action) => {
        const disabled = isFunction(action.dynamicDisabled)
          ? action.dynamicDisabled({ column: action, record, index, ...globalParams })
          : action.disabled;

        const handler = (action.onClick || actionHandler)?.bind(null, action, record, index);
        return {
          ...omit(action, IGNORE_BTN_PROPS),
          disabled,
          popConfirm: action.popConfirm
            ? {
                placement: 'topRight',
                ...action.popConfirm,
                title: action.popConfirm.title || 'common.deleteConfirm',
                confirm: handler,
              }
            : void 0,
          onClick: action.popConfirm ? void 0 : handler,
        } as ActionItem;
      });
  };

  const getAutoCreateKey = computed(() => {
    return unref(propsRef).autoCreateKey && !unref(propsRef).rowKey;
  });

  const getRowKey = computed(() => {
    const { rowKey = ROW_KEY } = unref(propsRef);
    return unref(getAutoCreateKey) ? ROW_KEY : (rowKey as string);
  });

  const validMapFns: Recordable<Recordable<() => Promise<boolean>>> = {};
  /** 执行编辑单元格控件的校验 */
  const runValidateFns = async (
    tableData: Recordable[],
    dataIndexList?: BasicColumn['dataIndex'][],
  ) => {
    const valids: boolean[] = [];
    const _list = dataIndexList || unref(getColumnsRef).map((i) => getDataIndex(i.dataIndex));
    for (const dataIndex of _list.filter(Boolean).map(getDataIndex)) {
      for (const record of tableData) {
        const fn = validMapFns[dataIndex]?.[record[getRowKey.value]];
        fn && valids.push(await fn());
      }
    }
    return valids.filter((i) => !i).length;
  };

  const globalParams = getGlobalParams();
  const { hasPermission } = usePermission();
  const getViewColumns = computed(() => {
    const { editable } = unref(propsRef);
    const viewColumns = sortFixedColumn(unref(getColumnsRef));

    const mapFn = (column) => {
      const { slots, flag } = column;

      if (!slots || !slots?.title) {
        column.customTitle = column.title;
      }
      const isDefaultAction = [INDEX_COLUMN_FLAG].includes(flag!);
      if (!column.customRender && !isDefaultAction) {
        if (editable && column.editComponent) {
          column.customRender = ({ text: value, record, index }) =>
            h(EditableCell, {
              setValidFn: (fn) =>
                set(validMapFns, [getDataIndex(column.dataIndex), record[getRowKey.value]], fn),
              value,
              record: { ...record },
              column,
              index,
            });
        } else if (column.render) {
          column.customRender = ({ text, record, index }) => {
            if (
              column.render.type === 'RenderPopover' &&
              isStringArray(column.render.popoverFields)
            ) {
              column.render.options = column.render.popoverFields.map((key) => {
                const col = unref(getColumnsRef).find((i) => key === getDataIndex(i.dataIndex));
                return {
                  value: col?.dataIndex ? get(record, col.dataIndex) : void 0,
                  label: col?.title,
                  langKey: col?.dataLangKey,
                };
              });
            }
            return h(RenderCell, { text, record, column, index });
          };
        } else if (column.format) {
          column.customRender = ({ text, record, index }) =>
            formatCell(text, column.format, record, index);
        } else if (column.flag === ACTION_COLUMN_FLAG && column.dataIndex === 'ACTION' && !slots) {
          column.customRender = ({ record, index }) =>
            h(TableAction, { actions: formatActions(record, index) });
        }
      }

      if (flag === 'PLACEHOLDER' && columnWidthSetting.value) {
        column.width = NaN;
        column.maxWidth = Infinity;
        column.minWidth = void 0;
      } else {
        column.minWidth = column.width as number;
        column.maxWidth = column.width as number;
      }

      return reactive(column) as BasicColumn;
    };

    const filteredColumns = cloneDeep(viewColumns).filter(
      (column) => hasPermission(column.auth) && isIfShow<BasicColumn>(column, globalParams),
    );

    const result: BasicColumn[] = [];
    for (const column of groupByGroupTitle(filteredColumns)) {
      const processedColumn = { ...column };

      if (processedColumn.children?.length) {
        processedColumn.children = processedColumn.children.map(mapFn);
      }

      result.push(mapFn(processedColumn));
    }
    result.push({ dataIndex: '_placeholder', flag: 'PLACEHOLDER', width: 1, resizable: true });

    return result;
  });

  watch(
    () => unref(propsRef).columns,
    (columns) => {
      columnsRef.value = columns;
      cacheColumns = columns?.filter((item) => !item.flag) ?? [];
    },
  );

  function setCacheColumnsByField(
    dataIndex: BasicColumn['dataIndex'],
    value: Partial<BasicColumn>,
  ) {
    if (!dataIndex || !value) {
      return;
    }
    cacheColumns.forEach((item) => {
      if (getDataIndex(item.dataIndex) === getDataIndex(dataIndex)) {
        Object.assign(item, value);
        return;
      }
    });
  }
  /**
   * set columns
   * @param columnList key｜column
   */
  function setColumns(
    columnList: Partial<BasicColumn>[] | (string | string[])[],
    isInner?: boolean,
  ) {
    const columns = cloneDeep(columnList);
    if (!isArray(columns)) return;

    isInner && mtsEmitter.emit('tableInnerChanged', true);

    if (columns.length <= 0) {
      columnsRef.value = [];
      return;
    }

    const firstColumn = columns[0];

    const cacheKeys = cacheColumns.map((item) => getDataIndex(item.dataIndex));

    if (!isString(firstColumn) && !isArray(firstColumn)) {
      columnsRef.value = (columns as BasicColumn[]).map((item) => ({
        ...item,
        width:
          item.width ??
          (item.flag ? columnsRef.value : cacheColumns).find(
            (i) => getDataIndex(i.dataIndex) === getDataIndex(item.dataIndex),
          )?.width,
      }));
    } else {
      const columnKeys = (columns as (string | string[])[]).map((m) => m.toString());
      const newColumns: BasicColumn[] = [];
      cacheColumns.forEach((item) => {
        newColumns.push({
          ...item,
          defaultHidden: !columnKeys.includes(item.dataIndex?.toString() || (item.key as string)),
        });
      });
      // Sort according to another array
      if (!isEqual(cacheKeys, columns)) {
        newColumns.sort((prev, next) => {
          return (
            columnKeys.indexOf(prev.dataIndex?.toString() as string) -
            columnKeys.indexOf(next.dataIndex?.toString() as string)
          );
        });
      }
      columnsRef.value = newColumns;
    }
    isMounted.value = true;
  }

  function getColumns(opt?: GetColumnsParams) {
    const { ignoreIndex, ignoreAction, ignoreDynamic, sort } = opt || {};
    let columns = toRaw(unref(getColumnsRef));
    if (ignoreIndex) {
      columns = columns.filter((item) => item.flag !== INDEX_COLUMN_FLAG);
    }
    if (ignoreAction) {
      columns = columns.filter((item) => item.flag !== ACTION_COLUMN_FLAG);
    }
    if (ignoreDynamic) {
      columns = columns.filter((item) => item.flag !== 'DYNAMIC');
    }

    if (sort) {
      columns = sortFixedColumn(columns);
    }

    return columns;
  }
  function getCacheColumns() {
    return cacheColumns;
  }
  function setCacheColumns(columns: BasicColumn[]) {
    if (!isArray(columns)) return;
    cacheColumns = columns.filter((item) => !item.flag);
  }
  /**
   * 拖拽列宽修改列的宽度
   */
  const columnWidthSetting = ref(false);
  function setColumnWidth(width: number, col: ColumnType<BasicColumn>) {
    columnWidthSetting.value = true;
    col.maxWidth = Infinity;
    col.minWidth = void 0;
    mtsEmitter.emit('tableWidthChanged', col);
    columnsRef.value.find((i) => getDataIndex(i.dataIndex) === getDataIndex(col.dataIndex))!.width =
      width;
    col.width = width;
    setCacheColumnsByField(col.dataIndex, { width });
  }

  return {
    getColumnsRef,
    getCacheColumns,
    getColumns,
    setColumns,
    setColumnWidth,
    getViewColumns,
    setCacheColumnsByField,
    setCacheColumns,
    toggleActionColumn,
    runValidateFns,
  };
}

function sortFixedColumn(columns: BasicColumn[]) {
  const fixedLeftColumns: BasicColumn[] = [];
  const fixedRightColumns: BasicColumn[] = [];
  const defColumns: BasicColumn[] = [];
  for (const column of columns) {
    if (column.fixed === 'left') {
      fixedLeftColumns.push(column);
      continue;
    }
    if (column.fixed === 'right') {
      fixedRightColumns.push(column);
      continue;
    }
    defColumns.push(column);
  }
  // 筛选逻辑
  const filterFunc = (item) => !item.defaultHidden;
  // 筛选首层显示列（1级表头）
  const viewColumns = [...fixedLeftColumns, ...defColumns, ...fixedRightColumns].filter(filterFunc);
  // 筛选>=2级表头（深度优先）
  const list = [...viewColumns];
  while (list.length) {
    const current = list[0];
    if (isArray(current.children)) {
      current.children = current.children.filter(filterFunc);
      list.shift();
      list.unshift(...current.children);
    } else {
      list.shift();
    }
  }
  return viewColumns;
}
